/**
 * The Search page's filter controls.
 *
 * One dropdown per DIMENSION — time, groups, cameras, event type, and each appearance
 * attribute — instead of a single menu holding all of them. The single menu was the first cut
 * and it does not survive contact with a real estate: choosing one of a hundred cameras meant
 * scrolling past every colour and every model to reach it, and the control gave no indication
 * of what was already selected. A labelled button per dimension makes the current state
 * readable without opening anything, and puts each list behind the label a person is looking
 * for.
 *
 * Everything here is presentation. The vocabularies, the counts and what is selectable all
 * arrive as props from the page, which gets them from the server's schema and facet endpoints —
 * so a control can never offer a value the API would reject.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

// ─── Dropdown shell ────────────────────────────────────────────────────────

export interface FilterDropdownProps {
  /** The dimension's name — always visible on the button, so the bar reads as a sentence. */
  label: string;
  /** The current selection, summarised. "All" when nothing is chosen. */
  summary: string;
  /** Draws the button as active — something is selected on this dimension. */
  active?: boolean;
  disabled?: boolean;
  /** Why it is disabled. Shown as the title, so a dead control always explains itself. */
  disabledReason?: string;
  /** Rendered left of the label — a colour swatch for the colour dimensions. */
  adornment?: ReactNode;
  children: (close: () => void) => ReactNode;
  /** Right-aligns the panel, for controls near the end of the row. */
  align?: 'left' | 'right';
  panelClassName?: string;
  /**
   * `pill` (default) is the compact inline button. `row` fills its container — label on the
   * left, the current choice on the right — the shape a stacked filter panel needs, where pills
   * of uneven width wrapped into a ragged block that was hard to scan.
   */
  variant?: 'pill' | 'row';
}

export function FilterDropdown({
  label,
  summary,
  active = false,
  disabled = false,
  disabledReason,
  adornment,
  children,
  align = 'left',
  panelClassName = 'w-64',
  variant = 'pill',
}: FilterDropdownProps) {
  const row = variant === 'row';
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  /*
   * The panel is `position: fixed`, placed from the button's rect, rather than absolute under
   * it. The filters live in a narrow, scrolling side panel, and an absolute panel inside a
   * scroll container is clipped by it — a 288px list opening from a button near the panel's
   * right edge would be cut in half. Fixed escapes the clip; the placement below keeps it on
   * screen (flipped above the button when there is no room below, clamped at both sides) and
   * follows the button when the side panel scrolls.
   */
  useLayoutEffect(() => {
    if (!open) { setPanelStyle({ visibility: 'hidden' }); return undefined; }
    const place = () => {
      const btn = buttonRef.current?.getBoundingClientRect();
      const panel = panelRef.current;
      if (!btn || !panel) return;
      const gap = 4;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = panel.offsetWidth;
      const below = vh - btn.bottom - gap - margin;
      const above = btn.top - gap - margin;
      const natural = Math.min(panel.scrollHeight, 416); // max-h-[26rem]
      const flip = below < Math.min(natural, 240) && above > below;
      const maxHeight = Math.max(120, Math.min(416, flip ? above : below));
      let left = align === 'right' ? btn.right - width : btn.left;
      left = Math.max(margin, Math.min(left, vw - width - margin));
      setPanelStyle(flip
        ? { position: 'fixed', left, bottom: vh - btn.top + gap, maxHeight }
        : { position: 'fixed', left, top: btn.bottom + gap, maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  // Close on an outside click or Escape. Both, because either alone leaves one of the two
  // habits people actually have (click away / hit escape) doing nothing.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={row ? 'relative w-full' : 'relative'}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex items-center gap-1.5 border transition-colors',
          // A row is a full-width control in the filter column, sized like the inputs beside it.
          row ? 'w-full rounded-lg bg-card px-3 py-2 text-[13px]' : 'rounded-md px-2.5 py-1.5 text-xs',
          disabled
            ? 'cursor-not-allowed border-border/60 text-muted-foreground/50'
            : active
              ? 'border-brand bg-brand-light text-foreground'
              : 'border-border text-foreground hover:bg-muted',
        ].join(' ')}
      >
        {row ? (
          <>
            <span className="shrink-0 text-muted-foreground">{label}</span>
            <span className="ml-auto flex min-w-0 items-center gap-1.5">
              {adornment}
              <span className={`truncate ${active ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{summary}</span>
            </span>
          </>
        ) : (
          <>
            {adornment}
            <span className="text-muted-foreground">{label}:</span>
            <span className="max-w-[11rem] truncate font-medium">{summary}</span>
          </>
        )}
        <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          id={panelId}
          ref={panelRef}
          style={panelStyle}
          className={`fixed z-50 overflow-auto rounded-md border border-border bg-card p-1.5 shadow-lg ${panelClassName}`}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

// ─── Option list ───────────────────────────────────────────────────────────

export interface FilterOption {
  value: string;
  label: string;
  /** Events carrying this value in the current window. Undefined = unknown, not zero. */
  count?: number;
  /** Groups options under a heading — "People", "Vehicles", "Safety". */
  group?: string;
  /** Offered but not choosable, with the reason on hover. */
  disabled?: boolean;
  disabledReason?: string;
  adornment?: ReactNode;
}

export interface OptionListProps {
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Single-select collapses to one value and closes the panel on pick. */
  mode?: 'multi' | 'single';
  /** Shows a search box. Worth it past ~12 options; essential for cameras. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Sits under the search box — e.g. how groups and cameras combine. */
  hint?: string;
  emptyText?: string;
  close?: () => void;
}

export function OptionList({
  options,
  selected,
  onChange,
  mode = 'multi',
  searchable = false,
  searchPlaceholder = 'Search…',
  hint,
  emptyText = 'Nothing to choose from',
  close,
}: OptionListProps) {
  const [term, setTerm] = useState('');

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase();
    return t ? options.filter((o) => o.label.toLowerCase().includes(t)) : options;
  }, [options, term]);

  /** Options in declaration order, but bucketed by heading. */
  const grouped = useMemo(() => {
    const buckets = new Map<string, FilterOption[]>();
    for (const o of visible) {
      const key = o.group || '';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(o);
    }
    return [...buckets.entries()];
  }, [visible]);

  const toggle = (value: string) => {
    if (mode === 'single') {
      onChange([value]);
      close?.();
      return;
    }
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const selectableValues = visible.filter((o) => !o.disabled).map((o) => o.value);
  const allSelected = selectableValues.length > 0 && selectableValues.every((v) => selected.includes(v));

  return (
    <div>
      {searchable && (
        <div className="relative mb-1.5">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded border border-border bg-background py-1 pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          />
        </div>
      )}

      {hint && <p className="mb-1.5 px-1 text-[10px] leading-snug text-muted-foreground">{hint}</p>}

      {/* Bulk controls only for multi-select, and only when there is more than one thing to
          act on — a "Select all" over a single option is noise. */}
      {mode === 'multi' && selectableValues.length > 1 && (
        <div className="mb-1 flex items-center justify-between border-b border-border px-1 pb-1">
          <button
            type="button"
            onClick={() => onChange(allSelected
              ? selected.filter((v) => !selectableValues.includes(v))
              : [...new Set([...selected, ...selectableValues])])}
            className="text-[10px] font-medium uppercase tracking-wide text-brand hover:underline"
          >
            {allSelected ? 'Clear these' : `Select all ${term ? 'matching' : ''}`.trim()}
          </button>
          {selected.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{selected.length} selected</span>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="px-2 py-3 text-center text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        grouped.map(([heading, opts]) => (
          <div key={heading || '_'} className="mb-1">
            {heading && (
              <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {heading}
              </div>
            )}
            {opts.map((o) => {
              const isOn = selected.includes(o.value);
              // A count of 0 dims the row but leaves it selectable — same rule as the swatch
              // grid. Nothing in scope is not the same as nothing possible.
              const empty = o.count === 0 && !isOn && !o.disabled;
              return (
                <button
                  key={o.value}
                  type="button"
                  disabled={o.disabled}
                  title={o.disabled ? o.disabledReason : undefined}
                  onClick={() => toggle(o.value)}
                  className={[
                    'flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs',
                    o.disabled ? 'cursor-not-allowed text-muted-foreground/50' : 'text-foreground hover:bg-muted',
                    empty ? 'opacity-50 hover:opacity-100' : '',
                  ].join(' ')}
                >
                  {mode === 'multi' && (
                    <span className={[
                      'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
                      isOn ? 'border-brand bg-brand text-white' : 'border-border',
                    ].join(' ')}>
                      {isOn && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} />}
                    </span>
                  )}
                  {o.adornment}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {typeof o.count === 'number' && (
                    <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{o.count}</span>
                  )}
                  {mode === 'single' && isOn && <CheckIcon className="h-3 w-3 shrink-0 text-brand" />}
                </button>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Colour swatches ───────────────────────────────────────────────────────

/**
 * Hex, not Tailwind classes: the values arrive as data, and a class name built by
 * interpolation is exactly what Tailwind's compiler cannot see and therefore does not emit.
 */
export const COLOR_SWATCH: Record<string, string> = {
  red: '#dc2626', orange: '#ea580c', yellow: '#ca8a04', green: '#16a34a',
  blue: '#2563eb', purple: '#9333ea', pink: '#db2777', brown: '#78350f',
  black: '#111827', white: '#f9fafb', grey: '#6b7280',
};

export function Swatch({ value, className = '' }: { value: string; className?: string }) {
  const hex = COLOR_SWATCH[value];
  if (!hex) return null;
  return (
    <span
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-border ${className}`}
      style={{ backgroundColor: hex }}
      aria-hidden="true"
    />
  );
}

/**
 * A grid of colour chips, for the two garment-colour dimensions.
 *
 * A grid rather than the vertical checkbox list the other dimensions use, because colour is
 * recognised rather than read: eleven swatches in three rows are taken in at a glance, where
 * eleven words have to be scanned one by one. `multi` and `unknown` have no swatch and stay in
 * the list below, where their labels can carry the meaning.
 */
export function SwatchGrid({
  values,
  selected,
  counts,
  onChange,
  notice,
}: {
  /** The FULL vocabulary — every colour the system can record, whether seen yet or not. */
  values: string[];
  selected: string[];
  /** Events per colour in the current scope. A missing or 0 count dims but never removes. */
  counts?: Record<string, number>;
  onChange: (next: string[]) => void;
  /** Shown above the grid when the scope has no appearance data at all. */
  notice?: string;
}) {
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const swatched = values.filter((v) => COLOR_SWATCH[v]);
  const worded = values.filter((v) => !COLOR_SWATCH[v]);

  return (
    <div>
      {/* Says the OR out loud. A grid of swatches looks like a radio group — people pick one and
          assume the next click replaces it, so the multi-select has to be stated rather than
          discovered. The count and Clear also make an accidental fifth selection easy to undo. */}
      <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-border px-1 pb-1.5">
        <span className="text-[10px] leading-snug text-muted-foreground">
          Pick several — <strong className="font-semibold text-foreground">any</strong> of them matches
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-brand hover:underline"
          >
            Clear {selected.length}
          </button>
        )}
      </div>

      {notice && (
        <p className="mb-1.5 rounded bg-muted px-1.5 py-1 text-[10px] leading-snug text-muted-foreground">
          {notice}
        </p>
      )}

      <div className="grid grid-cols-4 gap-1">
        {swatched.map((v) => {
          const isOn = selected.includes(v);
          const n = counts?.[v] ?? 0;
          // Zero is DIMMED, never hidden or disabled. Every colour the system can record stays
          // pickable: an operator setting up "find a blue shirt" before any blue has been seen
          // is asking a perfectly good question, and a palette that shows only what happened
          // already reads as a broken control. Searching a 0 returns 0 — honest, and quite
          // different from accepting an off-vocabulary word that can never match anything.
          const empty = n === 0 && !isOn;
          return (
            <button
              key={v}
              type="button"
              onClick={() => toggle(v)}
              title={`${v} — ${n} event${n === 1 ? '' : 's'} in this scope`}
              className={[
                'flex flex-col items-center gap-1 rounded border px-1 py-1.5 transition-colors',
                isOn ? 'border-brand bg-brand-light' : 'border-border hover:bg-muted',
                empty ? 'opacity-45 hover:opacity-100' : '',
              ].join(' ')}
            >
              {/* The tick sits ON the swatch, not beside it: on a 4-column grid a separate
                  checkbox column costs more width than the swatch itself, and a coloured dot
                  with a tick in it is unambiguous even for white and black. */}
              <span className="relative flex h-4 w-4 items-center justify-center">
                <span
                  className="absolute inset-0 rounded-full ring-1 ring-border"
                  style={{ backgroundColor: COLOR_SWATCH[v] }}
                />
                {isOn && (
                  <CheckIcon
                    className={`relative h-3 w-3 ${v === 'white' || v === 'yellow' ? 'text-black' : 'text-white'}`}
                    strokeWidth={3}
                  />
                )}
              </span>
              <span className={`w-full truncate text-center text-[9px] capitalize ${isOn ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{v}</span>
              <span className="font-mono text-[9px] leading-none text-muted-foreground">{n}</span>
            </button>
          );
        })}
      </div>

      {worded.length > 0 && (
        <div className="mt-1.5 border-t border-border pt-1.5">
          {worded.map((v) => {
            const isOn = selected.includes(v);
            const n = counts?.[v] ?? 0;
            const empty = n === 0 && !isOn;
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggle(v)}
                title={`${v} — ${n} event${n === 1 ? '' : 's'} in this scope`}
                className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs text-foreground hover:bg-muted ${empty ? 'opacity-45 hover:opacity-100' : ''}`}
              >
                <span className={[
                  'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
                  isOn ? 'border-brand bg-brand text-white' : 'border-border',
                ].join(' ')}>
                  {isOn && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} />}
                </span>
                <span className="flex-1 capitalize">{v}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{n}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Active-filter chip ────────────────────────────────────────────────────

/**
 * One active filter, as a removable chip.
 *
 * The dropdowns say what CAN be chosen; the chip row says what IS chosen, in one line, without
 * opening anything. It is also the fastest way to undo one filter out of six.
 */
export function ActiveChip({
  label,
  onRemove,
  adornment,
}: {
  /** "Dimension: value" — the dimension is drawn quietly and the value in bold. */
  label: string;
  /** Absent for a chip that only REPORTS a default (the time window) — nothing to remove. */
  onRemove?: () => void;
  adornment?: ReactNode;
}) {
  const cut = label.indexOf(': ');
  const name = cut > 0 ? label.slice(0, cut) : '';
  const value = cut > 0 ? label.slice(cut + 2) : label;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[13px] text-foreground shadow-sm">
      {adornment}
      {name && <span className="shrink-0 text-muted-foreground">{name}</span>}
      <span className="truncate font-semibold">{value}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={`Remove filter ${label}`}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}
